/**
 * Creates a generic tracker for us to use in react-track / framework context
 *
 * @author Nick Dreckshage <ndreckshage@wayfair.com>
 * @copyright 2020 Wayfair LLC - All rights reserved
 */
/**
 * NOTE: from string utls...
 * An implementation of Java's String.hashCode() method
 * http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
 */
const hashCode = ({str, salt = ''}) => {
  let hash = 0;
  const hashString = `${str}${salt}`;
  if (hashString.length === 0) {
    return hash;
  }

  for (let i = 0; i < hashString.length; i++) {
    const char = hashString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
};

export const formatAction = (
  payload,
  {defaultEventType, eventTypesToFormat}
) => {
  if (!payload.eventType) {
    payload.eventType = defaultEventType;
  }
  const {actionData, actionName, eventType, ...rest} = payload;

  if (!eventTypesToFormat.includes(eventType)) {
    const {actionName, actionData, ...rest} = payload;
    return rest;
  }

  // Return early if no data, adding the default eventType if missing
  if (actionData == null && !Object.keys(rest).length) {
    return {eventType, actionName};
  }

  return {
    eventType,
    actionName,
    actionData: actionData || rest,
  };
};

const computePayloadHash = payload =>
  hashCode({str: JSON.stringify(payload), salt: '1984'});

// Maybe send tracking event based on the options passed in.
// We wrap this method in the Component instances for easier unit tests.
export const maybeTrack = ({
  payload,
  once,
  track,
  hashes,
  defaultEventType,
  eventTypesToFormat,
}) => {
  if (once) {
    const payloadHash = computePayloadHash(payload);
    if (hashes.includes(payloadHash)) {
      return;
    }

    hashes.push(payloadHash);
  }

  if (typeof window === 'undefined') {
    return;
  }

  track(formatAction(payload, {defaultEventType, eventTypesToFormat}));
};

// Tracker is a generic interface on top of a tracking engine (google analytics / scribe / etc).
// trackingEngine should conform to a defined API. tracker can add additional functionality
// on top of the trackingEngine (like only sending an event once / formatting data)
const createTracker = ({
  trackingEngine,
  defaultEventType,
  eventTypesToFormat,
}) => {
  const hashes = [];

  return {
    track(payload, {once = false} = {}) {
      maybeTrack({
        track: trackingEngine.sendEvent,
        payload,
        hashes,
        once,
        defaultEventType,
        eventTypesToFormat,
      });
    },
    recordEvent: trackingEngine.recordEvent,
    setClickLocation: trackingEngine.setClickLocation,
    eventTypes: trackingEngine.eventTypes,
    visibility: trackingEngine.visibility,
    defaultEventType,
    pendingInView: trackingEngine.pendingInView,
  };
};

export default createTracker;
